import { EditorContent, useEditor } from '@tiptap/react';
import { Highlight } from '@tiptap/extension-highlight';
import { StarterKit } from '@tiptap/starter-kit';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { Typography } from '@tiptap/extension-typography';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FontSize } from './extentions/FontSize';
import styled from 'styled-components';
import { FormattingMenu } from './components/FormattingMenu';
import { colors } from '../../config/styles';
import { LinkMenu, LinkMenuMode } from './components/LinkMenu';
import { FloatingMenu } from './components/FloatingMenu';
import { EMPTY_RICH_TEXT_CONTENT } from './constants';
import { MaybeNil } from '../../utils/interfaces';
import { useOnClickOutside } from '../../utils/hooks';
import { theme } from '../../styles/theme';

const extensions = [
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3],
    },
    // Not supported (yet)
    blockquote: false,
    code: false,
    codeBlock: false,
    strike: false,
  }),
  Underline,
  Highlight,
  TextStyle,
  FontSize,
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    // we don't support justify
    alignments: ['left', 'center', 'right'],
  }),
  Link.extend({
    // This setting is required to mark boundaries (start or end of the link)
    // non-inclusive to the link. There are 2 reasons:
    // 1. You can "escape" the link by continue typing (see the video in
    // https://github.com/ueberdosis/tiptap/issues/3293)
    // 2. The Link menu visibility is controlled whether you are inside a link or now
    // Moving the cursor to the end of the link is the way to close the menu after save/cancel in the menu
    inclusive: false,
  }).configure({
    defaultProtocol: 'https',
    openOnClick: false,
    HTMLAttributes: {
      target: '_blank',
    },
  }),
  Typography,
];

const Wrapper = styled.div`
  height: 100%;
  min-width: 100%;
`;

const StyledEditor = styled(EditorContent)`
  height: 100%;
  width: 100%;

  .ProseMirror:focus {
    outline: none;
  }

  .tiptap {
    padding: 8px;

    // Base style
    font-size: 12px;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: normal;
    letter-spacing: normal;
    color: ${theme.colors.secondary900};

    p {
      margin-bottom: 8px;
    }

    h1 {
      font-size: 32px;
      line-height: 1.5;
      font-weight: 600;
      margin: 0;
      padding: 0;
    }

    h2 {
      font-size: 20px;
      line-height: 1.8;
      font-weight: 600;
      margin: 0;
      padding: 0;
    }

    h3 {
      font-size: 16px;
      line-height: 1.8;
      font-weight: 600;
      margin: 0;
      padding: 0;
    }

    a {
      color: ${theme.colors.mainBlue};
      text-decoration: underline;
    }

    mark {
      background-color: ${colors.contentRemoveHighlight};
    }
  }
`;

export interface IRichTextEditorRef {
  focus: () => void;
  select: () => void;
  reset: (newValue: MaybeNil<string>) => void;
}

export interface IRichTextEditorProps {
  value: MaybeNil<string>;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

export const RichTextEditor = forwardRef<
  IRichTextEditorRef,
  IRichTextEditorProps
>(({ value, onChange, disabled }, ref) => {
  const [isFocused, setIsFocused] = useState(false);

  const editorUpdated = useRef(false);
  const editor = useEditor({
    extensions,
    content: value ?? EMPTY_RICH_TEXT_CONTENT,
    onFocus: () => {
      setIsFocused(true);
    },
    onUpdate: () => {
      // This event is fired when a user modifies the contents of the editor
      // This flag means that there are content and formatting modifications
      // which can be submitted to onChange callback. Moreover, we can't compare
      // editor.getHTML() vs value to determine if there is a change because
      // there might be whitespace changes caused by the HTML Sanitization
      // tool that preserves HTML semantics but the HTML is char-by-char
      // different. For example, this HTML comes from Kobol:
      // <p><span style="font-size: 120%">aaaaa</span></p>
      // but once injected into the editor and returned back, it is:
      // <p><span style="font-size:120%">aaaaa</span></p>
      // (notice the lack of space before 120%)
      // This mechanism for tracking changes is not perfect (e.g. it doesn't
      // detect if user types "a" and then deletes it) but good enough
      editorUpdated.current = true;
    },
  });

  // If the prop `value` has changed, it effectively overwrites user updates
  // that's why we set editorUpdated back to false
  useEffect(() => {
    editorUpdated.current = false;
  }, [value]);

  useEffect(() => {
    editor?.setEditable(!disabled, false);
  }, [disabled, editor]);

  // expose focus / select methods, so it will behave like a normal input
  useImperativeHandle(
    ref,
    () => ({
      focus: () => editor?.commands.focus(),
      select: () => editor?.commands.selectAll(),
      reset: (newValue: MaybeNil<string>) =>
        editor?.commands.setContent(newValue ?? EMPTY_RICH_TEXT_CONTENT),
    }),
    [editor],
  );

  // formatting shows when there is selection
  const shouldShowFormattingMenu =
    (!disabled && editor && isFocused && !editor.state.selection.empty) ??
    false;

  // link appear only on empty selection and you are over link mark
  // What are marks?
  // https://discuss.prosemirror.net/t/discussion-what-are-marks/862
  const shouldShowEditLinkMenu =
    (!disabled &&
      editor &&
      isFocused &&
      editor.state.selection.empty &&
      editor.isActive('link')) ??
    false;

  const containerRef = useRef<HTMLDivElement>(null);
  const handleTriggerOnChange = () => {
    if (editorUpdated.current && onChange && editor) {
      onChange(editor.getHTML());
    }

    if (isFocused) {
      setIsFocused(false);
    }
  };

  // submit the changes when user "exits" the editor. Note that we can't
  // use onBlur because formatting menu operations cause a short blur
  // (because user's focus is now on the Bold button for example)
  // and focus again
  useOnClickOutside(containerRef, handleTriggerOnChange);

  return (
    <Wrapper ref={containerRef}>
      {editor && (
        <>
          <FloatingMenu editor={editor} open={shouldShowFormattingMenu}>
            <FormattingMenu editor={editor} />
          </FloatingMenu>
          <FloatingMenu editor={editor} open={shouldShowEditLinkMenu}>
            <LinkMenu editor={editor} mode={LinkMenuMode.Edit} />
          </FloatingMenu>
        </>
      )}
      <StyledEditor editor={editor} />
    </Wrapper>
  );
});
